import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useFormDraftPersistence } from '../../hooks/useFormDraftPersistence';

// Define the structure for a single company option (matching DashboardMain)
interface CompanyOption {
  value: string;
  label: string;
  logo: string; 
}

// Define form data structure
export interface AddManualAccountFormData {
  company: string;
  accountName: string;
  currentValue: number;
  isHidden: boolean;
}

// Define component props
interface AddManualAccountFormProps {
  onSubmit: (data: AddManualAccountFormData) => void;
  onBackClick: () => void;
  existingAccountNames: string[];
  // Add the companyOptions prop here
  companyOptions: CompanyOption[]; 
}

// Create Zod schema dynamically to include unique name check
const createAddManualAccountSchema = (existingNames: string[]) => z.object({
  company: z.string().min(1, 'Please select a company'),
  accountName: z.string()
    .min(1, 'Account name is required')
    .refine((name) => !existingNames.includes(name.trim()), {
      message: 'This account name already exists',
    }),
  currentValue: z.number({
      required_error: "Current value is required",
      invalid_type_error: "Value must be a number",
    })
    .min(0, 'Value must be zero or positive'),
  isHidden: z.boolean(),
});

const AddManualAccountForm: React.FC<AddManualAccountFormProps> = ({
  onSubmit,
  onBackClick,
  existingAccountNames,
  // Destructure the companyOptions prop
  companyOptions, 
}) => {
  const schema = createAddManualAccountSchema(existingAccountNames);
  const [isProcessingSubmit, setIsProcessingSubmit] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<AddManualAccountFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
        company: '', // Ensure default value for select
        accountName: '',
        isHidden: false,
        // currentValue: undefined // or 0, depending on preference
    }
  });

  // Persist form draft to sessionStorage
  const { clearDraft } = useFormDraftPersistence(
    'addManualAccount',
    null, // No accountId for new accounts
    watch,
    reset,
    {
      enabled: true,
    }
  );

  const handleFormSubmit: SubmitHandler<AddManualAccountFormData> = async (data) => {
    if (isProcessingSubmit) return;
    setIsProcessingSubmit(true);
    
    try {
      await onSubmit(data);
      // Clear draft on successful submit
      clearDraft();
    } finally {
      setIsProcessingSubmit(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Company Selection - This section should now use the prop */}
      <div>
        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
          Company
        </label>
        <select
          id="company"
          {...register('company')}
          className={`block w-full px-3 py-2 border ${
            errors.company ? 'border-red-300' : 'border-gray-300'
          } rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
        >
          <option value="" disabled>Select a company...</option>
          {/* This map will now use the companyOptions prop */}
          {companyOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {errors.company && (
          <p className="mt-2 text-sm text-red-600">{errors.company.message}</p>
        )}
      </div>

      {/* Account Name Input */}
      <div>
        <label htmlFor="accountName" className="block text-sm font-medium text-gray-700 mb-1">
          Account Name
        </label>
        <input
          id="accountName"
          type="text"
          {...register('accountName')}
          className={`appearance-none block w-full px-3 py-2 border ${
            errors.accountName ? 'border-red-300' : 'border-gray-300'
          } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
          placeholder="e.g., My Brokerage Account"
        />
        {errors.accountName && (
          <p className="mt-2 text-sm text-red-600">{errors.accountName.message}</p>
        )}
      </div>

      {/* Current Value Input */}
      <div>
        <label htmlFor="currentValue" className="block text-sm font-medium text-gray-700 mb-1">
          Current Account Value
        </label>
        <div className="relative mt-1 rounded-md shadow-sm">
           <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span className="text-gray-500 sm:text-sm">$</span>
            </div>
            <input
              id="currentValue"
              type="number"
              step="0.01" // Allow decimals
              {...register('currentValue', { valueAsNumber: true })} // Parse as number
              className={`block w-full rounded-md border py-2 pl-7 pr-3 sm:text-sm ${errors.currentValue ? 'border-red-300' : 'border-gray-300'} focus:outline-none focus:ring-green-500 focus:border-green-500`}
              placeholder="0.00"
            />
        </div>
        {errors.currentValue && (
          <p className="mt-2 text-sm text-red-600">{errors.currentValue.message}</p>
        )}
      </div>

      <label className="flex items-center space-x-3">
        <input
          type="checkbox"
          {...register('isHidden')}
          className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
        />
        <span className="text-sm font-medium text-gray-700">Hide account</span>
      </label>

      {/* Footer Buttons within the Form for Modal Content */}
      <div className="flex justify-end space-x-3 pt-4">
         {/* Back button removed - handled by modal header now */}
        <button
          type="submit"
          disabled={isProcessingSubmit}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 text-sm font-medium flex items-center justify-center"
        >
          {isProcessingSubmit && (
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          {isProcessingSubmit ? 'Saving...' : 'Next'}
        </button>
      </div>
    </form>
  );
};

export default AddManualAccountForm; 
